import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface SubscriptionContextType {
  subscribed: boolean;
  isTrialing: boolean;
  isFreeExploring: boolean;
  freeAccessEndsAt: string | null;
  productId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  user: User | null;
  checkSubscription: (opts?: { force?: boolean }) => Promise<void>;
  createCheckout: (billingPeriod?: "monthly" | "annual") => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [isFreeExploring, setIsFreeExploring] = useState(false);
  const [freeAccessEndsAt, setFreeAccessEndsAt] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const checkSubscription = async (opts?: { force?: boolean }) => {
    try {
      // Get current session to ensure we have a valid token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user) {
        setSubscribed(false);
        setIsTrialing(false);
        setProductId(null);
        setSubscriptionEnd(null);
        setLoading(false);
        return;
      }

      const currentUser = session.user;

      // Admins (developers/staff) always get full access.
      const { data: isAdmin } = await supabase.rpc('has_role', {
        _user_id: currentUser.id,
        _role: 'admin',
      });
      if (isAdmin === true) {
        setSubscribed(true);
        setIsTrialing(false);
        setIsFreeExploring(false);
        setFreeAccessEndsAt(null);
        setProductId('admin');
        setSubscriptionEnd(null);
        setLoading(false);
        return;
      }

      // Check database for trial AND free exploration period
      const { data: profile } = await supabase
        .from('profiles')
        .select('trial_ends_at, free_access_ends_at, has_started_trial')
        .eq('user_id', currentUser.id)
        .single();

      const now = new Date();
      
      // Check free exploration period (5 days before trial)
      const freeAccessEnd = profile?.free_access_ends_at ? new Date(profile.free_access_ends_at) : null;
      const isInFreeExploration = freeAccessEnd ? freeAccessEnd > now && !profile?.has_started_trial : false;
      
      // Check trial period (14 days after card entered)
      const trialEndsAt = profile?.trial_ends_at ? new Date(profile.trial_ends_at) : null;
      const isInTrial = trialEndsAt ? trialEndsAt > now : false;

      // Check Stripe subscription only if we have a valid session
      const { data: stripeData, error } = await supabase.functions.invoke('check-subscription', {
        body: opts?.force ? { force: true } : undefined,
      });
      
      if (error) {
        console.error('Error checking subscription:', error);
        // If Stripe check fails but user is in trial or free exploration, allow access
        setSubscribed(isInTrial || isInFreeExploration);
        setIsTrialing(isInTrial);
        setIsFreeExploring(isInFreeExploration);
        setFreeAccessEndsAt(freeAccessEnd?.toISOString() || null);
        setSubscriptionEnd(trialEndsAt?.toISOString() || null);
        setLoading(false);
        return;
      }

      // User has access if they're in free exploration, trial, OR have active subscription
      const hasAccess = isInFreeExploration || isInTrial || stripeData.subscribed || false;
      setSubscribed(hasAccess);
      setIsTrialing(isInTrial && !stripeData.subscribed);
      setIsFreeExploring(isInFreeExploration);
      setFreeAccessEndsAt(freeAccessEnd?.toISOString() || null);
      setProductId(stripeData.product_id || null);
      setSubscriptionEnd(
        isInTrial ? trialEndsAt?.toISOString() || null : stripeData.subscription_end || null
      );
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscribed(false);
      setIsTrialing(false);
      setIsFreeExploring(false);
      setFreeAccessEndsAt(null);
    } finally {
      setLoading(false);
    }
  };

  const createCheckout = async (billingPeriod: "monthly" | "annual" = "monthly") => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { billingPeriod }
      });
      
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Check subscription on mount
    const initSubscription = async () => {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      setUser(currentUser);
      if (currentUser) {
        await checkSubscription();
      } else {
        setLoading(false);
      }
    };

    initSubscription();

    // If Stripe just redirected back with ?subscription=success, force a fresh
    // Stripe check so premium access is granted immediately (not after 10 min).
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("subscription") === "success") {
        // Small delay to let auth session settle.
        setTimeout(() => checkSubscription({ force: true }), 250);
        params.delete("subscription");
        const qs = params.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
        );
      }
    } catch {
      /* ignore */
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        // Defer subscription check to avoid blocking auth callback
        setTimeout(() => {
          checkSubscription();
        }, 0);
      } else {
        setSubscribed(false);
        setIsTrialing(false);
        setProductId(null);
        setSubscriptionEnd(null);
        setLoading(false);
      }
    });

    // Re-check when the user returns to the tab (covers "just came back from Stripe checkout"
    // and stale caches without hammering Stripe every minute).
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkSubscription();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);


  return (
    <SubscriptionContext.Provider
      value={{
        subscribed,
        isTrialing,
        isFreeExploring,
        freeAccessEndsAt,
        productId,
        subscriptionEnd,
        loading,
        user,
        checkSubscription,
        createCheckout,
        openCustomerPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
